// Business mock components — real HTML/CSS, not images.
// Each business has a distinct brand direction so the three sites don't feel like the same template recoloured.

import { Phone, Star, ChevronRight, Menu } from "lucide-react";

/* -----------------------------------------------------------
   CHROME COMPONENTS
   ----------------------------------------------------------- */

export const BrowserChrome = ({ url, dark = false, children, className = "" }) => (
  <div className={`rounded-md overflow-hidden border border-[color:var(--border)] shadow-[0_20px_50px_-25px_rgba(11,28,46,0.35)] bg-white ${className}`}>
    <div className={`flex items-center gap-1.5 px-3 py-2 border-b ${dark ? "bg-[#151A21] border-black/40" : "bg-[#EFECE4] border-[color:var(--border-soft)]"}`}>
      <span className="w-2 h-2 rounded-full bg-[#E76F51]" />
      <span className="w-2 h-2 rounded-full bg-[#F4A261]" />
      <span className="w-2 h-2 rounded-full bg-[#2A9D8F]" />
      <div className={`ml-3 flex-1 h-5 rounded flex items-center px-2.5 ${dark ? "bg-white/5" : "bg-white/70 border border-[color:var(--border-soft)]"}`}>
        <span className={`text-[10px] tracking-tight ${dark ? "text-white/60" : "text-[color:var(--muted)]"} font-medium`}>{url}</span>
      </div>
    </div>
    {children}
  </div>
);

export const PhoneChrome = ({ children, className = "" }) => (
  <div className={`rounded-[28px] bg-[#0A0A0A] p-[6px] shadow-[0_20px_40px_-15px_rgba(11,28,46,0.5)] ${className}`}>
    <div className="relative w-full rounded-[22px] overflow-hidden bg-white">
      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-14 h-4 bg-black rounded-full z-20" />
      {children}
    </div>
  </div>
);

/* -----------------------------------------------------------
   1. NORTHLINE PLUMBING & HEATING
   Palette: dusty steel-blue hero, deep navy for nav/CTA, white sections.
   Modern lead-gen aesthetic — matches user reference brief.
   ----------------------------------------------------------- */
const NORTHLINE = {
  name: "Northline Plumbing & Heating",
  short: "Northline",
  monogram: "NL",
  url: "northlineplumbing.co.uk",
  phone: "0151 244 8090",
  navy: "#1B3B5B",
  navyDark: "#122A44",
  hero: "#7CA5C7",
  soft: "#F3F6FA",
  services: [
    { t: "Our Services", body: "Boiler repairs, bathroom fits, emergency plumbing." },
    { t: "Why Choose Us", body: "Gas Safe, fixed pricing, 10-year installation warranty." },
    { t: "Customer Reviews", body: "4.9 · 214 five-star Google reviews across Merseyside." },
  ],
};

// Real plumber action shots supplied by the client — moody, blue-lit, tradesman at work.
const PLUMB_HERO = "https://customer-assets-39nsmqrw.emergentagent.net/job_888d19bb-6d1f-4406-ae28-13dde7109b69/artifacts/th7aaond_timur-shakerzianov-VAGbjhkxg6I-unsplash.webp";
const PLUMB_PORTRAIT = "https://customer-assets-39nsmqrw.emergentagent.net/job_888d19bb-6d1f-4406-ae28-13dde7109b69/artifacts/39316ys4_timur-shakerzianov-c314Gh8dXAo-unsplash.webp";
// Kept for backwards compatibility with any other reference in this file.
const PLUMB_PHOTO = PLUMB_HERO;

// Tiny inline icons used in the feature row — no lucide (keeps the mock self-contained).
const _NLIcon = ({ variant }) => {
  const stroke = NORTHLINE.navy;
  if (variant === "services") return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a5 5 0 0 0-7.4 0l-3 3 3 3 3-3"/><path d="m9.3 17.7 3 3 3-3a5 5 0 0 0 0-7.4l-1.5-1.5"/></svg>
  );
  if (variant === "why") return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3Z"/><path d="m9 12 2 2 4-4"/></svg>
  );
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H7l-4 4V6a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z"/></svg>
  );
};

export const NorthlineDesktop = () => (
  <BrowserChrome url={NORTHLINE.url}>
    {/* Nav — white with dark navy CTA */}
    <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-black/5">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-sm flex items-center justify-center" style={{ background: NORTHLINE.navy }}>
          <span className="font-northline font-bold text-white text-[9px] leading-none">{NORTHLINE.monogram}</span>
        </div>
        <span className="font-northline font-bold text-[15px] tracking-tight" style={{ color: NORTHLINE.navy }}>{NORTHLINE.short}</span>
      </div>
      <div className="flex items-center gap-4 text-[11px] font-medium" style={{ color: NORTHLINE.navy }}>
        <span className="opacity-70">Services</span>
        <span className="opacity-70">Our Work</span>
        <span className="opacity-70">About</span>
        <span className="opacity-70">Reviews</span>
      </div>
      <span className="text-[10px] font-semibold text-white px-3 py-1.5 rounded-md" style={{ background: NORTHLINE.navy }}>
        Contact Us
      </span>
    </div>

    {/* HERO — dusty blue */}
    <div className="relative" style={{ background: NORTHLINE.hero }}>
      <div className="grid grid-cols-12 items-stretch">
        <div className="col-span-7 px-8 pt-10 pb-8 text-white">
          <p className="font-northline font-bold text-[36px] leading-[0.95] tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Expert<br />Plumbing<br />Repairs
          </p>
          <p className="text-[10px] leading-relaxed text-white/85 mt-4 max-w-[240px]">
            Gas Safe engineers across Liverpool and the Wirral — fixed-price boiler repairs, emergency plumbing and full bathroom fits, done properly.
          </p>
          <div className="mt-6 flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-white px-3 py-2 rounded-md" style={{ background: NORTHLINE.navy }}>
              Book a Survey
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-widest px-3 py-2 rounded-md border border-white text-white">
              Call Now
            </span>
          </div>
          <div className="mt-6 flex items-center gap-1.5">
            <span className="w-3 h-1 rounded-full bg-white" />
            <span className="w-1 h-1 rounded-full bg-white/50" />
            <span className="w-1 h-1 rounded-full bg-white/50" />
          </div>
        </div>
        <div className="col-span-5 relative min-h-[240px]">
          <img src={PLUMB_HERO} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '50% 30%' }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${NORTHLINE.hero} 0%, transparent 22%)` }} />
        </div>
      </div>
    </div>

    {/* FEATURES — 3-column on white */}
    <div className="px-8 py-9 bg-white">
      <div className="text-center max-w-[320px] mx-auto">
        <p className="font-northline font-bold text-[18px]" style={{ color: NORTHLINE.navy }}>Fully insured. Locally trusted.</p>
        <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
          Family-run team of Gas Safe engineers — every job backed by a 10-year installation warranty and same-day appointments across Merseyside.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-5 mt-7">
        {NORTHLINE.services.map((s, i) => (
          <div key={s.t} className="text-center">
            <div className="w-9 h-9 rounded-md flex items-center justify-center mx-auto mb-2.5" style={{ background: `${NORTHLINE.navy}0F` }}>
              <_NLIcon variant={i === 0 ? "services" : i === 1 ? "why" : "reviews"} />
            </div>
            <p className="font-northline font-semibold text-[12px]" style={{ color: NORTHLINE.navy }}>{s.t}</p>
            <p className="text-[9px] leading-relaxed text-slate-500 mt-1.5">{s.body}</p>
          </div>
        ))}
      </div>
    </div>

    {/* SPLIT SECTION — Professional Plumbing Repairs */}
    <div className="grid grid-cols-12 gap-6 px-8 pt-6 pb-9 items-center" style={{ background: NORTHLINE.soft }}>
      <div className="col-span-7">
        <p className="text-[9px] font-semibold uppercase tracking-[0.2em]" style={{ color: NORTHLINE.navy, opacity: 0.6 }}>Full-service</p>
        <p className="font-northline font-bold text-[22px] leading-[1.05] tracking-tight mt-2" style={{ color: NORTHLINE.navy, letterSpacing: '-0.02em' }}>
          Professional<br />Plumbing Repairs
        </p>
        <p className="text-[10px] leading-relaxed mt-3 max-w-[280px]" style={{ color: NORTHLINE.navy, opacity: 0.72 }}>
          Copper pipework, combi installations, bathroom refits and emergency callouts — every job comes with a clear fixed-price quote and Gas Safe certificate.
        </p>
        <div className="mt-5 flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-white px-3 py-2 rounded-md" style={{ background: NORTHLINE.navy }}>
            Get Quote
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-widest px-3 py-2 rounded-md" style={{ color: NORTHLINE.navy }}>
            Read More
          </span>
        </div>
      </div>
      <div className="col-span-5">
        <div className="rounded-2xl overflow-hidden aspect-[4/5] shadow-[0_20px_40px_-20px_rgba(11,28,46,0.35)]" style={{ background: NORTHLINE.hero }}>
          <img src={PLUMB_PORTRAIT} alt="" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="mt-3 flex items-center justify-center gap-1.5">
          <span className="w-2.5 h-1 rounded-full" style={{ background: NORTHLINE.navy }} />
          <span className="w-1 h-1 rounded-full" style={{ background: NORTHLINE.navy, opacity: 0.3 }} />
        </div>
      </div>
    </div>
  </BrowserChrome>
);

export const NorthlineMobileSite = () => (
  <>
    {/* Nav — white, matches desktop */}
    <div className="flex items-center justify-between px-4 pt-7 pb-2.5 bg-white border-b border-black/5">
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded-sm flex items-center justify-center" style={{ background: NORTHLINE.navy }}>
          <span className="font-northline font-bold text-white text-[6px] leading-none">{NORTHLINE.monogram}</span>
        </div>
        <span className="font-northline font-bold text-[11px]" style={{ color: NORTHLINE.navy }}>{NORTHLINE.short}</span>
      </div>
      <Menu size={11} style={{ color: NORTHLINE.navy }} />
    </div>

    {/* Hero — dusty blue with full-width photo underneath, mirrors desktop stacked */}
    <div style={{ background: NORTHLINE.hero }} className="text-white">
      <div className="px-4 pt-4 pb-3">
        <p className="font-northline font-bold text-[19px] leading-[0.95] tracking-tight" style={{ letterSpacing: '-0.02em' }}>
          Expert<br />Plumbing<br />Repairs
        </p>
        <p className="text-[7px] leading-relaxed text-white/85 mt-2">
          Gas Safe engineers across Liverpool &amp; the Wirral. Fixed-price boiler repairs, emergency plumbing, full bathroom fits.
        </p>
        <div className="mt-3 flex items-center gap-1.5">
          <span className="text-[7px] font-semibold uppercase tracking-widest text-white px-2 py-1 rounded font-northline" style={{ background: NORTHLINE.navy }}>Book Survey</span>
          <span className="text-[7px] font-semibold uppercase tracking-widest px-2 py-1 rounded border border-white text-white font-northline">Call</span>
        </div>
        <div className="mt-3 flex items-center gap-1">
          <span className="w-2 h-0.5 rounded-full bg-white" />
          <span className="w-0.5 h-0.5 rounded-full bg-white/50" />
          <span className="w-0.5 h-0.5 rounded-full bg-white/50" />
        </div>
      </div>
      <div className="aspect-[4/3] relative">
        <img src={PLUMB_HERO} alt="" loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: '50% 30%' }} />
      </div>
    </div>

    {/* Feature stack — mirrors desktop 3-icon row */}
    <div className="bg-white px-4 py-5">
      <p className="text-center font-northline font-bold text-[12px]" style={{ color: NORTHLINE.navy }}>Fully insured. Locally trusted.</p>
      <p className="text-center text-[7px] text-slate-500 mt-1 leading-relaxed">
        10-year installation warranty, same-day appointments.
      </p>
      <div className="mt-4 space-y-3">
        {NORTHLINE.services.map((s, i) => (
          <div key={s.t} className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${NORTHLINE.navy}0F` }}>
              <_NLIcon variant={i === 0 ? "services" : i === 1 ? "why" : "reviews"} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-northline font-semibold text-[9px]" style={{ color: NORTHLINE.navy }}>{s.t}</p>
              <p className="text-[7px] leading-relaxed text-slate-500 mt-0.5">{s.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Split section — mirrors desktop "Professional Plumbing Repairs" */}
    <div className="px-4 py-5" style={{ background: NORTHLINE.soft }}>
      <p className="text-[7px] font-semibold uppercase tracking-[0.2em]" style={{ color: NORTHLINE.navy, opacity: 0.6 }}>Full-service</p>
      <p className="font-northline font-bold text-[15px] leading-[1.05] tracking-tight mt-1.5" style={{ color: NORTHLINE.navy, letterSpacing: '-0.02em' }}>
        Professional<br />Plumbing Repairs
      </p>
      <p className="text-[7px] leading-relaxed mt-2" style={{ color: NORTHLINE.navy, opacity: 0.72 }}>
        Copper pipework, combi installations, bathroom refits and emergency callouts — every job comes with a fixed-price quote and Gas Safe certificate.
      </p>
      <div className="mt-3 rounded-lg overflow-hidden aspect-[4/3]" style={{ background: NORTHLINE.hero }}>
        <img src={PLUMB_PORTRAIT} alt="" loading="lazy" className="w-full h-full object-cover" />
      </div>
      <div className="mt-3 flex items-center gap-1.5">
        <span className="text-[7px] font-semibold uppercase tracking-widest text-white px-2 py-1 rounded font-northline" style={{ background: NORTHLINE.navy }}>Get Quote</span>
        <span className="text-[7px] font-semibold uppercase tracking-widest px-2 py-1 rounded font-northline" style={{ color: NORTHLINE.navy }}>Read More</span>
      </div>
    </div>
  </>
);

export const NorthlinePhone = ({ className = "" }) => (
  <PhoneChrome className={className}>
    <NorthlineMobileSite />
  </PhoneChrome>
);

/* -----------------------------------------------------------
   2. HAWTHORN ELECTRICAL
   Palette: deep navy #0F1A2E + bright yellow #FFC700, muted white.
   Modern lead-gen electrician aesthetic per client reference.
   ----------------------------------------------------------- */
const HAWTHORN = {
  name: "Hawthorn Electrical",
  short: "Hawthorn",
  monogram: "H",
  url: "hawthornelectrical.co.uk",
  navy: "#0F1A2E",
  navyLift: "#152640",
  yellow: "#FFC700",
  yellowSoft: "#FFD73D",
  services: [
    { t: "Rewiring", body: "Full house rewires and consumer-unit upgrades." },
    { t: "EV Chargers", body: "OZEV-approved installs from major brands." },
    { t: "Solar & Battery", body: "MCS-accredited PV and storage systems." },
    { t: "Fault Finding", body: "Same-day diagnostics with fixed-price fix." },
    { t: "Certifications", body: "EICR, PAT and landlord compliance reports." },
    { t: "Commercial", body: "Office, warehouse and retail fit-outs." },
  ],
};

// Real client action shots (Raze Solar + Jimmy Nilsson Masth, uploaded by client).
const ELEC_HERO = "https://customer-assets-39nsmqrw.emergentagent.net/job_888d19bb-6d1f-4406-ae28-13dde7109b69/artifacts/zun2z3mt_raze-solar-_aSFmmvS62I-unsplash.webp";
const ELEC_SPLIT = "https://customer-assets-39nsmqrw.emergentagent.net/job_888d19bb-6d1f-4406-ae28-13dde7109b69/artifacts/t0gql8zg_jimmy-nilsson-masth-UovTD1dG-lA-unsplash.webp";
const ELEC_MACRO = "https://customer-assets-39nsmqrw.emergentagent.net/job_888d19bb-6d1f-4406-ae28-13dde7109b69/artifacts/ob3chma7_jimmy-nilsson-masth-U3GiK1u5j24-unsplash.webp";
// Preserve older reference so unrelated code compiles.
const ELEC_PHOTO = ELEC_HERO;

// Six mini icons for the feature grid — matches the reference layout.
const _HWIcon = ({ variant }) => {
  const s = HAWTHORN.navy;
  const props = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none", stroke: s, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  if (variant === "rewire") return (<svg {...props}><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z"/></svg>);
  if (variant === "ev") return (<svg {...props}><rect x="3" y="6" width="12" height="14" rx="1"/><path d="M15 10h3a2 2 0 0 1 2 2v4"/><path d="M7 10h4"/><path d="M7 14h4"/></svg>);
  if (variant === "solar") return (<svg {...props}><circle cx="12" cy="12" r="3.5"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4"/></svg>);
  if (variant === "fault") return (<svg {...props}><path d="M12 2a10 10 0 1 0 10 10"/><path d="M22 2 12 12"/></svg>);
  if (variant === "cert") return (<svg {...props}><path d="M20 7 9 18l-5-5"/></svg>);
  return (<svg {...props}><rect x="3" y="4" width="18" height="14" rx="1"/><path d="M8 20h8M12 18v2"/></svg>);
};

export const HawthornDesktop = () => (
  <BrowserChrome url={HAWTHORN.url} dark>
    <div style={{ background: HAWTHORN.navy, color: "#fff" }}>
      {/* Nav */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: HAWTHORN.yellow }}>
            <span className="font-hawthorn font-bold text-[10px] leading-none" style={{ color: HAWTHORN.navy }}>{HAWTHORN.monogram}</span>
          </div>
          <span className="font-hawthorn font-bold text-[15px]">Hawthorn</span>
        </div>
        <div className="flex items-center gap-4 text-[11px] font-medium text-white/70">
          <span>Services</span><span>Solar</span><span>About</span><span>Reviews</span>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-md font-hawthorn" style={{ background: HAWTHORN.yellow, color: HAWTHORN.navy }}>
          Free Quote
        </span>
      </div>

      {/* HERO — split with photo right */}
      <div className="grid grid-cols-12 items-stretch">
        <div className="col-span-6 px-8 pt-10 pb-9">
          <p className="text-[9px] font-semibold tracking-[0.3em] uppercase text-white/50 mb-3">— Manchester &amp; Cheshire</p>
          <p className="font-hawthorn font-bold text-[36px] leading-[0.95] tracking-tight" style={{ letterSpacing: '-0.025em' }}>
            Need an <span style={{ color: HAWTHORN.yellow }}>electrician</span> you can trust?
          </p>
          <p className="text-[10px] leading-relaxed mt-4 max-w-[300px] text-white/65">
            NICEIC-approved electricians covering rewires, EV chargers, solar &amp; battery, and full commercial fit-outs — with a Part-P certificate on every job.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <span className="relative inline-block">
              <span className="absolute inset-0 blur-lg opacity-70 rounded-md" style={{ background: HAWTHORN.yellow }} />
              <span className="relative font-hawthorn font-bold text-[10px] uppercase tracking-widest px-4 py-2.5 rounded-md inline-flex items-center gap-1.5" style={{ background: HAWTHORN.yellow, color: HAWTHORN.navy }}>
                Book a Survey <ChevronRight size={11} strokeWidth={3} />
              </span>
            </span>
            <span className="text-[10px] font-medium text-white/50">or call 0161 305 1122</span>
          </div>
        </div>
        <div className="col-span-6 relative min-h-[280px] overflow-hidden">
          <img src={ELEC_HERO} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '50% 40%' }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${HAWTHORN.navy} 0%, transparent 22%)` }} />
        </div>
      </div>

      {/* 6-icon feature grid */}
      <div className="px-8 py-10 border-t border-white/5">
        <p className="text-center font-hawthorn font-bold text-[22px] leading-tight" style={{ letterSpacing: '-0.02em' }}>
          Everything you need, <span style={{ color: HAWTHORN.yellow }}>done properly.</span>
        </p>
        <div className="grid grid-cols-3 gap-6 mt-8">
          {HAWTHORN.services.map((s, i) => (
            <div key={s.t} className="text-left">
              <div className="w-9 h-9 rounded-full flex items-center justify-center mb-3" style={{ background: HAWTHORN.yellow }}>
                <_HWIcon variant={["rewire","ev","solar","fault","cert","commercial"][i]} />
              </div>
              <p className="font-hawthorn font-semibold text-[13px]">{s.t}</p>
              <p className="text-[9px] leading-relaxed text-white/55 mt-1.5">{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Split section — action shot + copy */}
      <div className="grid grid-cols-12 gap-0 border-t border-white/5">
        <div className="col-span-5 relative min-h-[220px] overflow-hidden">
          <img src={ELEC_SPLIT} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '60% 45%' }} />
        </div>
        <div className="col-span-7 px-8 py-9">
          <p className="text-[9px] font-semibold tracking-[0.3em] uppercase text-white/50 mb-3">— On the tools since 2016</p>
          <p className="font-hawthorn font-bold text-[24px] leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Trusted by <span style={{ color: HAWTHORN.yellow }}>420+</span> Manchester homes and businesses.
          </p>
          <p className="text-[10px] leading-relaxed mt-3 max-w-[320px] text-white/65">
            One team, one number, one point of contact. Every survey, quote and install handled by the same NICEIC-approved engineer from start to finish.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <span className="text-[9px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md" style={{ background: 'rgba(255,255,255,0.08)', color: HAWTHORN.yellow }}>NICEIC #571234</span>
            <span className="text-[9px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md" style={{ background: 'rgba(255,255,255,0.08)', color: HAWTHORN.yellow }}>MCS Accredited</span>
            <span className="text-[9px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md" style={{ background: 'rgba(255,255,255,0.08)', color: HAWTHORN.yellow }}>OZEV Approved</span>
          </div>
        </div>
      </div>

      {/* Bottom yellow CTA panel */}
      <div className="relative px-8 py-8 grid grid-cols-12 gap-6 items-center" style={{ background: HAWTHORN.yellow, color: HAWTHORN.navy }}>
        <div className="col-span-6">
          <p className="text-[9px] font-semibold tracking-[0.3em] uppercase" style={{ opacity: 0.6 }}>— All Enquiries Answered</p>
          <p className="font-hawthorn font-bold text-[26px] leading-[1.02] tracking-tight mt-2" style={{ letterSpacing: '-0.02em' }}>
            Get an honest fixed-price quote today.
          </p>
          <p className="text-[10px] leading-relaxed mt-2 max-w-[280px]" style={{ opacity: 0.72 }}>
            Tell us about the job and we&apos;ll come back within one working day with a clear quote — no obligation, no follow-up sales calls.
          </p>
        </div>
        <div className="col-span-6">
          <div className="bg-white rounded-lg p-4 shadow-[0_20px_40px_-20px_rgba(15,26,46,0.35)]">
            <p className="font-hawthorn font-bold text-[13px]" style={{ color: HAWTHORN.navy }}>Free Quote</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="h-6 rounded-md" style={{ background: '#F1F1F3' }} />
              <div className="h-6 rounded-md" style={{ background: '#F1F1F3' }} />
            </div>
            <div className="h-6 rounded-md mt-2" style={{ background: '#F1F1F3' }} />
            <div className="h-14 rounded-md mt-2" style={{ background: '#F1F1F3' }} />
            <div className="mt-3 text-center text-[10px] font-bold uppercase tracking-widest py-2 rounded-md font-hawthorn" style={{ background: HAWTHORN.navy, color: '#fff' }}>
              Send Enquiry
            </div>
          </div>
        </div>
      </div>
    </div>
  </BrowserChrome>
);

export const HawthornMobileSite = () => (
  <>
    {/* Nav — dark navy with yellow Free Quote pill */}
    <div style={{ background: HAWTHORN.navy }} className="flex items-center justify-between px-4 pt-7 pb-2.5 border-b border-white/5">
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: HAWTHORN.yellow }}>
          <span className="font-hawthorn font-bold text-[7px] leading-none" style={{ color: HAWTHORN.navy }}>{HAWTHORN.monogram}</span>
        </div>
        <span className="font-hawthorn font-bold text-[11px] text-white">Hawthorn</span>
      </div>
      <span className="text-[6px] font-bold uppercase tracking-widest px-2 py-1 rounded-md font-hawthorn" style={{ background: HAWTHORN.yellow, color: HAWTHORN.navy }}>
        Free Quote
      </span>
    </div>

    {/* Hero — stacked: copy then photo */}
    <div style={{ background: HAWTHORN.navy }} className="text-white">
      <div className="px-4 pt-4 pb-3">
        <p className="text-[7px] font-semibold tracking-[0.25em] uppercase text-white/50 mb-1.5">— Manchester &amp; Cheshire</p>
        <p className="font-hawthorn font-bold text-[19px] leading-[0.95] tracking-tight" style={{ letterSpacing: '-0.025em' }}>
          Need an <span style={{ color: HAWTHORN.yellow }}>electrician</span> you can trust?
        </p>
        <p className="text-[7px] leading-relaxed mt-2 text-white/65">
          NICEIC-approved covering rewires, EV chargers, solar &amp; battery, and full commercial fit-outs.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <span className="relative inline-block">
            <span className="absolute inset-0 blur-md opacity-70 rounded-md" style={{ background: HAWTHORN.yellow }} />
            <span className="relative font-hawthorn font-bold text-[7px] uppercase tracking-widest px-2.5 py-1.5 rounded-md inline-flex items-center gap-1" style={{ background: HAWTHORN.yellow, color: HAWTHORN.navy }}>
              Book a Survey <ChevronRight size={8} strokeWidth={3} />
            </span>
          </span>
          <span className="text-[7px] font-medium text-white/45">or call</span>
        </div>
      </div>
      <div className="aspect-[16/10] relative overflow-hidden">
        <img src={ELEC_HERO} alt="" loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: '50% 40%' }} />
      </div>
    </div>

    {/* Feature grid — 2 cols mirror of desktop 3-col */}
    <div style={{ background: HAWTHORN.navy }} className="px-4 py-5 border-t border-white/5">
      <p className="text-center font-hawthorn font-bold text-[13px] leading-tight text-white" style={{ letterSpacing: '-0.02em' }}>
        Everything you need, <span style={{ color: HAWTHORN.yellow }}>done properly.</span>
      </p>
      <div className="grid grid-cols-2 gap-3 mt-4">
        {HAWTHORN.services.map((s, i) => (
          <div key={s.t} className="text-left">
            <div className="w-6 h-6 rounded-full flex items-center justify-center mb-1.5" style={{ background: HAWTHORN.yellow }}>
              <_HWIcon variant={["rewire","ev","solar","fault","cert","commercial"][i]} />
            </div>
            <p className="font-hawthorn font-semibold text-[9px] text-white">{s.t}</p>
            <p className="text-[6.5px] leading-relaxed text-white/55 mt-0.5">{s.body}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Split section — action photo + copy */}
    <div style={{ background: HAWTHORN.navy }} className="border-t border-white/5">
      <div className="aspect-[16/10] relative overflow-hidden">
        <img src={ELEC_SPLIT} alt="" loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: '60% 45%' }} />
      </div>
      <div className="px-4 py-4 text-white">
        <p className="text-[7px] font-semibold tracking-[0.25em] uppercase text-white/50 mb-1.5">— On the tools since 2016</p>
        <p className="font-hawthorn font-bold text-[13px] leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.02em' }}>
          Trusted by <span style={{ color: HAWTHORN.yellow }}>420+</span> Manchester homes and businesses.
        </p>
        <div className="mt-2.5 flex flex-wrap items-center gap-1">
          <span className="text-[6px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(255,255,255,0.08)', color: HAWTHORN.yellow }}>NICEIC</span>
          <span className="text-[6px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(255,255,255,0.08)', color: HAWTHORN.yellow }}>MCS</span>
          <span className="text-[6px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(255,255,255,0.08)', color: HAWTHORN.yellow }}>OZEV</span>
        </div>
      </div>
    </div>

    {/* Yellow CTA panel with mocked form */}
    <div className="px-4 py-5" style={{ background: HAWTHORN.yellow, color: HAWTHORN.navy }}>
      <p className="text-[6px] font-semibold tracking-[0.25em] uppercase" style={{ opacity: 0.6 }}>— All Enquiries Answered</p>
      <p className="font-hawthorn font-bold text-[14px] leading-[1.05] tracking-tight mt-1" style={{ letterSpacing: '-0.02em' }}>
        Get an honest fixed-price quote today.
      </p>
      <div className="mt-3 bg-white rounded-lg p-3 shadow-[0_10px_20px_-10px_rgba(15,26,46,0.35)]">
        <p className="font-hawthorn font-bold text-[10px]" style={{ color: HAWTHORN.navy }}>Free Quote</p>
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <div className="h-4 rounded" style={{ background: '#F1F1F3' }} />
          <div className="h-4 rounded" style={{ background: '#F1F1F3' }} />
        </div>
        <div className="h-4 rounded mt-1.5" style={{ background: '#F1F1F3' }} />
        <div className="h-10 rounded mt-1.5" style={{ background: '#F1F1F3' }} />
        <div className="mt-2 text-center text-[7px] font-bold uppercase tracking-widest py-1.5 rounded font-hawthorn text-white" style={{ background: HAWTHORN.navy }}>
          Send Enquiry
        </div>
      </div>
    </div>
  </>
);

export const HawthornPhone = ({ className = "" }) => (
  <PhoneChrome className={className}>
    <HawthornMobileSite />
  </PhoneChrome>
);

/* -----------------------------------------------------------
   3. CASTLE GROUNDCARE (real client)
   Palette: forest green, warm stone, off-white
   ----------------------------------------------------------- */
const CASTLE = {
  name: "Castle Groundcare",
  short: "Castle",
  url: "castlegroundcare.co.uk",
  phone: "01371 828 890",
  green: "#1F4632",
  greenDark: "#12301F",
  stone: "#D8CDB7",
  cream: "#F5F1EA",
  services: ["Grounds maintenance", "Landscaping", "Hedge cutting", "Commercial maintenance"],
};
// Real landscape photography — labelled as Castle Groundcare in the composition
const CASTLE_PHOTO = "https://images.unsplash.com/photo-1558904541-efa843a96f01?q=80&w=1200&auto=format&fit=crop";

export const CastleDesktop = () => (
  <BrowserChrome url={CASTLE.url}>
    <div style={{ background: CASTLE.cream }}>
      {/* Nav */}
      <div className="flex items-center justify-between px-8 py-4 border-b" style={{ borderColor: `${CASTLE.green}22` }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: CASTLE.green }}>
            <span className="font-display text-white text-[11px] leading-none">C</span>
          </div>
          <div>
            <p className="font-display text-[15px] leading-none" style={{ color: CASTLE.greenDark }}>Castle Groundcare</p>
            <p className="text-[8px] uppercase tracking-widest mt-1" style={{ color: CASTLE.green, opacity: 0.7 }}>Est. 2004 · Essex</p>
          </div>
        </div>
        <div className="flex items-center gap-5 text-[11px] font-medium" style={{ color: CASTLE.greenDark }}>
          <span>Services</span><span>Projects</span><span>About</span><span>Contact</span>
        </div>
        <span className="text-[10px] font-semibold px-3 py-1.5 rounded-sm inline-flex items-center gap-1" style={{ background: CASTLE.green, color: CASTLE.cream }}>
          <Phone size={9} /> {CASTLE.phone}
        </span>
      </div>

      {/* Hero — image dominant */}
      <div className="relative aspect-[16/8]">
        <img src={CASTLE_PHOTO} alt="Castle Groundcare landscaping" loading="lazy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex items-end" style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(18, 48, 31, 0.7) 100%)' }}>
          <div className="p-10 text-white max-w-[520px]">
            <p className="text-[9px] font-semibold uppercase tracking-[0.3em] mb-3 opacity-80">— Commercial &amp; residential grounds care</p>
            <p className="font-display text-[32px] leading-[0.98] tracking-tight">
              Professional grounds care, <span className="italic">built around your property</span>.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <span className="text-[10px] font-semibold uppercase tracking-widest px-3 py-2 rounded-sm inline-flex items-center gap-1 text-white" style={{ background: CASTLE.green }}>
                Arrange a Site Visit <ChevronRight size={10} strokeWidth={3} />
              </span>
              <span className="text-[10px] font-medium opacity-80">Free quote · No obligation</span>
            </div>
          </div>
        </div>
      </div>

      {/* Services + testimonial */}
      <div className="grid grid-cols-12 gap-6 px-10 py-8">
        <div className="col-span-7">
          <p className="text-[9px] font-semibold tracking-[0.3em] uppercase mb-4" style={{ color: CASTLE.green, opacity: 0.7 }}>— What we look after</p>
          <div className="grid grid-cols-2 gap-3">
            {CASTLE.services.map((s, i) => (
              <div key={s} className="border rounded-sm p-3.5 flex items-center gap-3" style={{ borderColor: `${CASTLE.green}25`, background: 'white' }}>
                <span className="font-display text-[13px] tabular" style={{ color: CASTLE.green }}>0{i + 1}</span>
                <p className="font-display text-[13px]" style={{ color: CASTLE.greenDark }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-5 p-5 rounded-sm" style={{ background: CASTLE.stone }}>
          <p className="text-[9px] font-semibold tracking-widest uppercase mb-2" style={{ color: CASTLE.greenDark }}>Trusted by</p>
          <p className="font-display text-[13px] leading-tight italic" style={{ color: CASTLE.greenDark }}>
            "Been using Castle for our estate for eight years. Never a missed week, everything is left immaculate."
          </p>
          <p className="text-[9px] font-semibold mt-3" style={{ color: CASTLE.greenDark, opacity: 0.75 }}>— Estate Manager, Great Dunmow</p>
          <div className="mt-4 flex items-center gap-3 text-[8px] font-semibold uppercase tracking-widest" style={{ color: CASTLE.greenDark, opacity: 0.6 }}>
            <span>Public liability £10M</span>
            <span>·</span>
            <span>ISO 9001</span>
          </div>
        </div>
      </div>
    </div>
  </BrowserChrome>
);

export const CastlePhone = ({ className = "" }) => (
  <PhoneChrome className={className}>
    <div style={{ background: CASTLE.cream }}>
      <div className="flex items-center justify-between px-4 pt-7 pb-3">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: CASTLE.green }}>
            <span className="font-display text-white text-[7px] leading-none">C</span>
          </div>
          <span className="font-display text-[10px]" style={{ color: CASTLE.greenDark }}>Castle Groundcare</span>
        </div>
        <Menu size={12} style={{ color: CASTLE.greenDark }} />
      </div>
      <div className="aspect-[4/3] relative">
        <img src={CASTLE_PHOTO} alt="" loading="lazy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 p-3 flex items-end" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(18, 48, 31, 0.85) 100%)' }}>
          <p className="font-display text-white text-[13px] leading-tight">Grounds care, built around your <span className="italic">property</span>.</p>
        </div>
      </div>
      <div className="p-3">
        <div className="text-[8px] font-semibold uppercase tracking-widest text-white text-center py-2 rounded-sm" style={{ background: CASTLE.green }}>
          Arrange a Site Visit
        </div>
        <ul className="mt-3 space-y-1.5">
          {CASTLE.services.slice(0, 3).map((s, i) => (
            <li key={s} className="flex items-center gap-2 text-[8px]" style={{ color: CASTLE.greenDark }}>
              <span className="font-display tabular" style={{ color: CASTLE.green }}>0{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </PhoneChrome>
);

/* -----------------------------------------------------------
   BRANDED INVOICE (parameterised)
   ----------------------------------------------------------- */
const INVOICE_CONFIGS = {
  hawthorn: {
    name: "Hawthorn.", ink: "#1F2937", accent: "#F5B301", no: "INV-1108",
    client: "K. Prescott — Warehouse rewire", total: "£3,240.00",
    stampBg: "#FBECBD", stampInk: "#7A5500",
    lines: [["Warehouse rewire", "£2,450"], ["New consumer unit", "£520"], ["Certification & Part-P", "£270"]],
  },
  castle: {
    name: "Castle Groundcare", ink: "#1F4632", accent: "#1F4632", no: "INV-0219",
    client: "Ridgefield Estate — Q1 maintenance", total: "£6,780.00",
    stampBg: "#DDE8DE", stampInk: "#114B30",
    lines: [["Grounds maintenance", "£4,200"], ["Hedge cutting", "£1,850"], ["Planting scheme", "£730"]],
  },
  northline: {
    name: "Northline", ink: "#0B2545", accent: "#C0392B", no: "INV-0472",
    client: "Mr & Mrs Bradley — Combi installation", total: "£1,842.00",
    stampBg: "#F5D6D0", stampInk: "#7A1A11",
    lines: [["Combi boiler installation", "£1,450"], ["System flush", "£240"], ["Parts & fittings", "£152"]],
  },
};

export const InvoiceCard = ({ business = "northline", className = "" }) => {
  const cfg = INVOICE_CONFIGS[business] || INVOICE_CONFIGS.northline;

  return (
    <div className={`bg-white border border-[color:var(--border)] rounded-sm shadow-[0_15px_35px_-20px_rgba(11,28,46,0.3)] p-5 w-full max-w-[320px] ${className}`}>
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-widest text-[color:var(--muted)]">Invoice · Paid</p>
          <p className="font-display text-[15px] mt-1 tabular" style={{ color: cfg.ink }}>{cfg.no}</p>
        </div>
        <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{ background: cfg.accent, color: '#fff' }}>
          <span className="font-display text-[10px] leading-none">{cfg.name.charAt(0)}</span>
        </div>
      </div>
      <p className="text-[10px] text-[color:var(--muted)] mb-3">{cfg.client}</p>
      <div className="border-t border-[color:var(--border-soft)] py-3 space-y-1.5 text-[11px]">
        {cfg.lines.map(([l, v]) => (
          <div key={l} className="flex justify-between">
            <span className="text-[color:var(--ink)]">{l}</span>
            <span className="tabular" style={{ color: cfg.ink }}>{v}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-[color:var(--border-soft)] pt-3 flex items-baseline justify-between">
        <span className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Total inc. VAT</span>
        <span className="font-display text-[20px] tabular" style={{ color: cfg.ink }}>{cfg.total}</span>
      </div>
      <div className="mt-3 inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-widest px-2 py-1 rounded-sm" style={{ background: cfg.stampBg, color: cfg.stampInk }}>
        ✓ Paid · This month
      </div>
    </div>
  );
};

/* -----------------------------------------------------------
   BRAND SYSTEM (connected visual: logo · palette · desktop · mobile · Google · quote · invoice)
   Uses Northline for consistency across "What you receive"
   ----------------------------------------------------------- */
export const BrandSystem = () => (
  <div className="grid grid-cols-12 gap-4">
    {/* Label 01 · Your brand */}
    <div className="col-span-12 md:col-span-4 rounded-sm p-6 relative" style={{ background: NORTHLINE.navy, color: 'white' }}>
      <span className="grid-ref text-white/50 absolute top-4 left-4">01 · Your brand</span>
      <div className="mt-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: NORTHLINE.accent }}>
          <span className="font-display text-white text-[16px] leading-none">NL</span>
        </div>
        <div>
          <p className="font-display text-[16px] text-white leading-none">Northline</p>
          <p className="text-[8px] uppercase tracking-widest text-white/60 mt-1">Plumbing &amp; Heating</p>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-4 gap-1.5">
        {["#0B2545", "#132E56", "#C0392B", "#F5F1EA"].map((c) => (
          <div key={c} className="aspect-square rounded-sm border border-white/10" style={{ background: c }} />
        ))}
      </div>
      <p className="text-[9px] text-white/50 mt-3">Navy, red, cream — Bricolage Grotesque + Inter</p>
    </div>

    {/* 02 · Your website */}
    <div className="col-span-12 md:col-span-8 rounded-sm border border-[color:var(--border-soft)] p-6 relative bg-[color:var(--bg-paper)]">
      <span className="grid-ref absolute top-4 left-4">02 · Your website</span>
      <div className="mt-6 grid grid-cols-12 gap-4 items-end">
        <div className="col-span-8">
          <div className="scale-[0.85] origin-top-left w-[117.6%]">
            <NorthlineDesktop />
          </div>
        </div>
        <div className="col-span-4 flex justify-center">
          <NorthlinePhone className="w-full max-w-[150px]" />
        </div>
      </div>
    </div>

    {/* 03 · Your Google presence */}
    <div className="col-span-12 md:col-span-7 rounded-sm border border-[color:var(--border-soft)] bg-white p-6 relative">
      <span className="grid-ref absolute top-4 left-4">03 · Your Google presence</span>
      <div className="mt-8">
        <p className="text-[10px] text-[color:var(--muted)] tabular">google.com/search?q=plumber+liverpool</p>
        <div className="mt-3 space-y-2">
          <div>
            <p className="text-[10px] text-[color:var(--muted)]">northlineplumbing.co.uk</p>
            <p className="text-[14px] text-[#1a0dab] leading-tight">Northline Plumbing &amp; Heating · Liverpool</p>
            <p className="text-[11px] text-[color:var(--muted)] leading-relaxed mt-0.5 max-w-[400px]">
              Family-run Gas Safe plumbing and heating engineers covering Merseyside. Boiler installations, emergency plumbing, bathroom fits. Same-day appointments most days.
            </p>
          </div>
          <div className="border-t border-[color:var(--border-soft)] pt-2 flex items-center gap-3">
            <span className="text-[11px] font-semibold text-[color:var(--ink)] inline-flex items-center gap-1">
              <Star size={11} className="fill-[#F5B301] text-[#F5B301]" /> 4.9
            </span>
            <span className="text-[10px] text-[color:var(--muted)]">214 Google reviews · Gas Safe #571234</span>
          </div>
        </div>
      </div>
    </div>

    {/* 04 · Your quotes & invoices */}
    <div className="col-span-12 md:col-span-5 rounded-sm border border-[color:var(--border-soft)] p-6 relative bg-[color:var(--bg-paper)] flex flex-col items-center">
      <span className="grid-ref absolute top-4 left-4 self-start">04 · Your quotes &amp; invoices</span>
      <div className="mt-8">
        <InvoiceCard business="northline" />
      </div>
    </div>
  </div>
);
